/**
 * useDepositData - state and API logic for DepositManager
 */
import { useState, useEffect, useCallback } from 'react';
import depositService from '../../../services/depositService';
import { productService } from '../../../services/productService';

export function useDepositData() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadDeposits = useCallback(async () => {
    try {
      setLoading(true);
      const result = await depositService.getDeposits(true);
      if (result.success) {
        setDeposits(result.data || []);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Blad ladowania kaucji: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeposits();
  }, [loadDeposits]);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAddDeposit = async (newDeposit, selectedDepositProduct, onDone) => {
    if (!newDeposit.nazwa.trim()) { setError('Podaj nazwe kaucji'); return; }
    if (!newDeposit.kwota || parseFloat(newDeposit.kwota) <= 0) { setError('Podaj kwote kaucji wieksza od 0'); return; }

    try {
      setError('');
      const result = await depositService.createDeposit({
        nazwa: newDeposit.nazwa.trim(),
        kwota: parseFloat(newDeposit.kwota),
        opis: newDeposit.opis,
        produkt_id: selectedDepositProduct?.id || null,
        create_product: false
      });
      if (result.success) {
        showSuccess('Kaucja zostala utworzona. Teraz mozesz dodac do niej produkty.');
        loadDeposits();
        onDone();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Blad dodawania kaucji: ' + err.message);
    }
  };

  const handleSaveEdit = async (editingDeposit, onDone) => {
    if (!editingDeposit.nazwa.trim()) { setError('Nazwa kaucji jest wymagana'); return; }
    try {
      setError('');
      const result = await depositService.updateDeposit(editingDeposit.id, {
        nazwa: editingDeposit.nazwa,
        kwota: parseFloat(editingDeposit.kwota),
        opis: editingDeposit.opis,
        aktywny: editingDeposit.aktywny
      });
      if (result.success) {
        showSuccess('Kaucja zostala zaktualizowana');
        loadDeposits();
        onDone();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Blad zapisywania kaucji: ' + err.message);
    }
  };

  const handleDeleteDeposit = async (deposit, forceDelete = false) => {
    const actionText  = forceDelete ? 'TRWALE USUNAC' : 'dezaktywowac';
    const warningText = forceDelete
      ? `\n\nUWAGA: Ta operacja jest nieodwracalna! Kaucja "${deposit.nazwa}" zostanie permanentnie usunieta.`
      : '';
    if (!window.confirm(`Czy na pewno chcesz ${actionText} kaucje "${deposit.nazwa}"?${warningText}`)) return;

    try {
      setError('');
      const result = await depositService.deleteDeposit(deposit.id, forceDelete);
      if (result.success) {
        showSuccess(result.message || (forceDelete ? 'Kaucja zostala trwale usunieta' : 'Kaucja zostala dezaktywowana'));
        loadDeposits();
      } else {
        setError(result.message || 'Nieznany blad');
      }
    } catch (err) {
      setError('Blad usuwania kaucji: ' + err.message);
    }
  };

  const handleAssignDepositProductForDeposit = async (depositToAssign, product, onDone) => {
    try {
      setError('');
      const result = await depositService.updateDeposit(depositToAssign.id, { produkt_id: product.id });
      if (result.success) {
        showSuccess(`Produkt "${product.nazwa || product.name}" zostal przypisany jako kaucja`);
        loadDeposits();
        onDone();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Blad przypisywania produktu: ' + err.message);
    }
  };

  return {
    deposits, loading, error, success,
    setError, setSuccess,
    loadDeposits,
    handleAddDeposit,
    handleSaveEdit,
    handleDeleteDeposit,
    handleAssignDepositProductForDeposit
  };
}

export function useProductSearch(query, enabled = true) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!enabled || !query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const result = await productService.searchProducts({ search: query, limit: 20 });
        if (result && result.products) setResults(result.products);
        else if (Array.isArray(result)) setResults(result);
        else setResults([]);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, enabled]);

  return { results, searching };
}

export function useDepositProductsModal() {
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [depositProducts, setDepositProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const openModal = async (deposit) => {
    setSelectedDeposit(deposit);
    setShowProductModal(true);
    setProductSearch('');
    setSearchResults([]);
    try {
      const result = await depositService.getDepositProducts(deposit.id);
      if (result.success) setDepositProducts(result.data || []);
    } catch (_) {}
  };

  const closeModal = () => {
    setShowProductModal(false);
    setSelectedDeposit(null);
    setDepositProducts([]);
    setSearchResults([]);
    setProductSearch('');
  };

  useEffect(() => {
    if (!productSearch || productSearch.trim().length < 2 || !showProductModal) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const result = await productService.searchProducts({ search: productSearch, limit: 30 });
        let products = result?.products || (Array.isArray(result) ? result : []);
        const assignedIds = depositProducts.map(p => p.produkt_id);
        setSearchResults(products.filter(p => !assignedIds.includes(p.id)));
      } catch (_) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch, showProductModal, depositProducts]);

  const assignProduct = async (productId, onSuccess, onError) => {
    if (!selectedDeposit) return;
    try {
      const result = await depositService.assignProducts(selectedDeposit.id, [productId]);
      if (result.success) {
        const productsResult = await depositService.getDepositProducts(selectedDeposit.id);
        if (productsResult.success) setDepositProducts(productsResult.data || []);
        setSearchResults(prev => prev.filter(p => p.id !== productId));
        onSuccess('Produkt zostal przypisany');
      } else {
        onError(result.message);
      }
    } catch (err) {
      onError('Blad przypisywania produktu: ' + err.message);
    }
  };

  const removeProduct = async (productId, onSuccess, onError) => {
    if (!selectedDeposit) return;
    try {
      const result = await depositService.removeProduct(selectedDeposit.id, productId);
      if (result.success) {
        setDepositProducts(prev => prev.filter(p => p.produkt_id !== productId));
        onSuccess('Produkt zostal usuniety z kaucji');
      } else {
        onError(result.message);
      }
    } catch (err) {
      onError('Blad usuwania produktu: ' + err.message);
    }
  };

  return {
    selectedDeposit, depositProducts, showProductModal,
    productSearch, setProductSearch,
    searchResults, searchLoading,
    openModal, closeModal, assignProduct, removeProduct
  };
}
